import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { HeartPulse, CheckCircle, Loader2, AlertTriangle, Phone, Mail, Camera } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PreRegisterPage() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    // Person wearing bracelet
    person_name: "",
    person_photo: "",
    has_dementia: false,
    allergies: "",
    additional_notes: "",
    // Emergency contact / Guardian
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    contact_relationship: "",
    // Shipping
    shipping_address: "",
    shipping_city: "",
    shipping_state: "",
    shipping_zip: ""
  });

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Photo must be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(",")[1];
        setFormData({ ...formData, person_photo: base64 });
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.person_name || !formData.contact_name || !formData.contact_phone || !formData.contact_email) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/preregister`, formData);
      setSubmitted(true);
      toast.success("Order submitted successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit order");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 py-8 px-4">
        <div className="max-w-lg mx-auto text-center">
          <div className="mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_bracelet-alert/artifacts/suao9dvz_SFA%20Logo%20-%20long.png" 
              alt="Stress-Free Aging" 
              className="h-16 mx-auto"
            />
          </div>
          
          <Card className="border-teal-200 bg-teal-50">
            <CardContent className="pt-6">
              <CheckCircle className="h-16 w-16 text-teal-600 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-teal-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Order Received!
              </h1>
              <p className="text-teal-700 mb-6">
                Thank you for your order. We will contact you shortly to confirm payment and shipping details.
              </p>
              
              <div className="bg-white rounded-lg p-4 mb-6 text-left">
                <h3 className="font-bold text-slate-900 mb-2">Payment Instructions:</h3>
                <p className="text-slate-700 mb-3">Please send payment via Zelle to:</p>
                <div className="flex items-center justify-center gap-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_bracelet-alert/artifacts/d0mu1r15_Screenshot_20260201_165944_Word.jpg" 
                    alt="Zelle QR Code"
                    className="w-32 h-32 object-contain"
                  />
                  <div className="text-left">
                    <p className="font-bold text-purple-700">Stress Free Aging LLC</p>
                    <p className="text-slate-600 text-sm">Scan QR or search in Zelle</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-slate-600 text-sm">Questions? Contact us:</p>
                <p className="font-bold text-teal-700">(586) 306-8449</p>
                <p className="font-bold text-teal-700">stressfreeaging@gmail.com</p>
              </div>

              <Link to="/" className="block mt-6">
                <Button variant="outline">Return Home</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_bracelet-alert/artifacts/suao9dvz_SFA%20Logo%20-%20long.png" 
              alt="Stress-Free Aging" 
              className="h-14 mx-auto mb-2"
            />
          </Link>
          <h1 className="text-2xl font-bold text-teal-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Order Your SOS Bracelet
          </h1>
          <p className="text-slate-600">Fill out the form below to pre-register and order</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Person Wearing Bracelet */}
          <Card className="border-slate-200 shadow-lg mb-6">
            <CardHeader className="bg-teal-50 border-b border-teal-100">
              <CardTitle className="text-lg text-teal-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Person Wearing the Bracelet
              </CardTitle>
              <CardDescription>Information about the person who will wear the SOS bracelet</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              {/* Photo Upload */}
              <div className="text-center mb-4">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoChange}
                  accept="image/*"
                  className="hidden"
                  data-testid="photo-input"
                />
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="cursor-pointer inline-block"
                >
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt="Profile preview"
                      className="w-32 h-32 rounded-full object-cover border-4 border-teal-600 mx-auto"
                      data-testid="photo-preview"
                    />
                  ) : (
                    <div className="w-32 h-32 bg-slate-100 rounded-full flex flex-col items-center justify-center border-2 border-dashed border-slate-300 mx-auto hover:border-teal-500 transition-colors">
                      <Camera className="h-8 w-8 text-slate-400 mb-1" />
                      <span className="text-xs text-slate-500">Add Photo</span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-2">Tap to add a photo (recommended)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="person_name">Full Name *</Label>
                <Input
                  id="person_name"
                  placeholder="Enter full name"
                  value={formData.person_name}
                  onChange={(e) => setFormData({ ...formData, person_name: e.target.value })}
                  required
                  className="h-12"
                  data-testid="person-name-input"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-pink-50 rounded-lg border border-pink-200">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-pink-600" />
                  <div>
                    <Label htmlFor="has_dementia" className="text-pink-800 font-medium">
                      Has Dementia / Memory Issues
                    </Label>
                    <p className="text-sm text-pink-600">Alert responders to be patient</p>
                  </div>
                </div>
                <Switch
                  id="has_dementia"
                  checked={formData.has_dementia}
                  onCheckedChange={(checked) => setFormData({ ...formData, has_dementia: checked })}
                  data-testid="dementia-switch"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="allergies">Allergies (optional - keep brief for privacy)</Label>
                <Input
                  id="allergies"
                  placeholder="e.g., Penicillin"
                  value={formData.allergies}
                  onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="additional_notes">Additional Notes (optional)</Label>
                <Textarea
                  id="additional_notes"
                  placeholder="Any other important information..."
                  value={formData.additional_notes}
                  onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Emergency Contact */}
          <Card className="border-slate-200 shadow-lg mb-6">
            <CardHeader className="bg-teal-50 border-b border-teal-100">
              <CardTitle className="text-lg text-teal-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Emergency Contact / Guardian
              </CardTitle>
              <CardDescription>Who should be contacted in an emergency</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_name">Contact Name *</Label>
                  <Input
                    id="contact_name"
                    placeholder="e.g., John Smith"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    required
                    className="h-12"
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_relationship">Relationship</Label>
                  <Input
                    id="contact_relationship"
                    placeholder="e.g., Son, Daughter"
                    value={formData.contact_relationship}
                    onChange={(e) => setFormData({ ...formData, contact_relationship: e.target.value })}
                    className="h-12"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_phone">Phone Number *</Label>
                  <Input
                    id="contact_phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    required
                    className="h-12"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_email">Email *</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    placeholder="email@example.com"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    required
                    className="h-12"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Shipping Address */}
          <Card className="border-slate-200 shadow-lg mb-6">
            <CardHeader className="bg-teal-50 border-b border-teal-100">
              <CardTitle className="text-lg text-teal-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Shipping Address
              </CardTitle>
              <CardDescription>Where should we ship the bracelet?</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="shipping_address">Street Address *</Label>
                <Input
                  id="shipping_address"
                  placeholder="123 Main St"
                  value={formData.shipping_address}
                  onChange={(e) => setFormData({ ...formData, shipping_address: e.target.value })}
                  required
                  className="h-12"
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="shipping_city">City *</Label>
                  <Input
                    id="shipping_city"
                    placeholder="City"
                    value={formData.shipping_city}
                    onChange={(e) => setFormData({ ...formData, shipping_city: e.target.value })}
                    required
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shipping_state">State *</Label>
                  <Input
                    id="shipping_state"
                    placeholder="MI"
                    value={formData.shipping_state}
                    onChange={(e) => setFormData({ ...formData, shipping_state: e.target.value })}
                    required
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shipping_zip">ZIP *</Label>
                  <Input
                    id="shipping_zip"
                    placeholder="48000"
                    value={formData.shipping_zip}
                    onChange={(e) => setFormData({ ...formData, shipping_zip: e.target.value })}
                    required
                    className="h-12"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Info */}
          <Card className="border-purple-200 bg-purple-50 mb-6">
            <CardHeader>
              <CardTitle className="text-lg text-purple-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Payment via Zelle
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-white rounded-lg p-4 mb-4 text-center">
                <p className="text-sm text-purple-600 font-medium mb-1">PRE-ORDER SPECIAL</p>
                <p className="text-4xl font-bold text-purple-800">$49.00</p>
                <p className="text-sm text-slate-600 mt-1">First 100 orders only!</p>
                <p className="text-xs text-slate-500">Regular price: $59.00</p>
              </div>
              <div className="flex items-center gap-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_bracelet-alert/artifacts/d0mu1r15_Screenshot_20260201_165944_Word.jpg" 
                  alt="Zelle QR Code"
                  className="w-28 h-28 object-contain bg-white rounded-lg p-2"
                />
                <div>
                  <p className="text-purple-800 mb-2">
                    After submitting this form, please send <strong>$49.00</strong> via Zelle to <strong>Stress Free Aging LLC</strong>
                  </p>
                  <p className="text-purple-700 text-sm">
                    Your bracelet will ship after payment is confirmed.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full h-14 bg-teal-600 hover:bg-teal-700 text-white text-lg font-semibold"
            disabled={submitting}
            data-testid="submit-order-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="mr-2 h-5 w-5" />
                Submit Order
              </>
            )}
          </Button>

          <p className="text-center text-sm text-slate-500 mt-4">
            Questions? Call <a href="tel:5863068449" className="text-teal-600 font-medium">(586) 306-8449</a>
          </p>
        </form>
      </div>
    </div>
  );
}
